// API utility functions
// Prefer environment override, otherwise use the configured root path from Configuration Service

import { getRootPath, getBasePath } from './config';
import { supabase } from './supabase';

// Internal: normalize a base URL by removing any trailing slashes
function stripTrailingSlashes(url) {
  if (typeof url !== 'string') return '';
  // Preserve protocol like "http://" (but envs should be full origins). This regex removes only slashes
  // at the very end, leaving protocol delimiters intact.
  return url.replace(/\/+$/g, '');
}

// IMPORTANT: In Create React App, `process.env.REACT_APP_*` is statically replaced at build time.
// Do NOT guard on `typeof process` at runtime or you'll short‑circuit the value in the browser.
// Simply reference it directly so the bundler can inline the literal string (or `undefined`).
const rawEnv = process.env.REACT_APP_API_URL || '';
const cleanedEnv = typeof rawEnv === 'string' ? rawEnv.trim() : '';
const normalizedEnv = cleanedEnv ? stripTrailingSlashes(cleanedEnv) : '';

// Priority: REACT_APP_API_URL > getRootPath() > empty string
const configuredRootPath = getRootPath();
const fallbackPath = configuredRootPath ? stripTrailingSlashes(configuredRootPath) : '';

export const API_BASE_URL = normalizedEnv || fallbackPath || '';

/**
 * Fetch an ingested file from Supabase (via API) and open it in a new tab for viewing.
 * Uses authenticated fetch; works for PDFs and other blobs the browser can display inline.
 */
export async function openIngestionEntryFileForView({ suffix, groupSlug, entryId, field = 'file' }) {
  if (!suffix || !groupSlug || !entryId) {
    throw new Error('Missing horizon or group for file download');
  }
  const headers = await getAuthHeaders();
  const url = `${API_BASE_URL}/casc/v1/api/suffixes/${encodeURIComponent(suffix)}/groups/${encodeURIComponent(groupSlug)}/entries/${encodeURIComponent(String(entryId))}/file?field=${encodeURIComponent(field)}`;
  const res = await fetch(url, { headers });
  if (!res.ok) {
    let msg = `Failed to load file (${res.status})`;
    const errText = await res.text();
    try {
      const j = JSON.parse(errText);
      if (j && (j.error || j.message)) msg = String(j.error || j.message);
    } catch (_) {
      if (errText) msg = errText.slice(0, 300);
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const w = typeof window !== 'undefined' ? window.open(objectUrl, '_blank', 'noopener,noreferrer') : null;
  if (!w) {
    URL.revokeObjectURL(objectUrl);
    throw new Error('Popup blocked — allow popups to view the file, or download is disabled.');
  }
  setTimeout(() => {
    try {
      URL.revokeObjectURL(objectUrl);
    } catch (_) { /* ignore */ }
  }, 120000);
}

/**
 * Base URL for OAuth redirect URIs and docs: where `/casc/v1/api/...` is reachable in the browser.
 * When `REACT_APP_API_URL` is only the origin (no path) but the app is deployed under `BASE_PATH`
 * (e.g. `/9efeed`), this returns `origin + BASE_PATH` so values match Google Cloud redirect rules.
 */
export function getApiBaseForOAuthAndRedirects() {
  const base = stripTrailingSlashes(API_BASE_URL || '');
  const pageOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const fromRootPath = stripTrailingSlashes(getRootPath() || '');
  if (!base) {
    return fromRootPath || pageOrigin || '';
  }
  try {
    const u = new URL(base);
    const path = (u.pathname || '').replace(/\/+$/g, '');
    if (path && path !== '/') {
      return base;
    }
    if (pageOrigin && u.origin === pageOrigin && getBasePath()) {
      return stripTrailingSlashes(`${u.origin}${getBasePath()}`);
    }
  } catch (_) {
    return base;
  }
  return base;
}

// Log where the client will connect for API calls (once on import)
try {
  const pageOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const effectiveBase = API_BASE_URL || pageOrigin || '[current origin]';
  const healthUrl = `${effectiveBase}/casc/v1/api/health`;

  let source = 'current origin (relative URL)';
  if (normalizedEnv) {
    source = 'REACT_APP_API_URL';
  } else if (fallbackPath) {
    source = 'Configuration Service (getRootPath)';
  }

  console.log('[API] Server connection target:', {
    baseUrl: effectiveBase,
    healthCheckUrl: healthUrl,
    source,
  });
} catch (_) {
  // no-op in non-browser contexts
}

/**
 * Fetch settings from the API server
 * @returns {Promise<Object>} Settings object with supabaseUrl, supabaseKey (legacy fields may include documentFolderLink)
 */
export const fetchSettings = async () => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/settings`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch settings: ${response.statusText}`);
  }

  return await response.json();
};

/**
 * Save settings to the API server
 * @param {Object} settings - Settings object to save
 * @returns {Promise<Object>} Updated settings
 */
export const saveSettings = async (settings) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/settings`, {
    method: 'POST',
    headers,
    body: JSON.stringify(settings),
  });

  if (!response.ok) {
    throw new Error(`Failed to save settings: ${response.statusText}`);
  }

  return await response.json();
};

// Store the current session token to avoid repeated getSession() calls
let cachedSessionToken = null;
let cachedSessionExpiry = null;

/**
 * Get Supabase session token for API requests
 * Uses cached token if available and not expired, otherwise fetches from Supabase
 * @returns {Promise<string|null>} JWT token or null
 */
async function getAuthToken() {
  try {
    // Check if we have a cached token that's still valid
    if (cachedSessionToken && cachedSessionExpiry && Date.now() < cachedSessionExpiry) {
      return cachedSessionToken;
    }
    
    // Try to get session with retry (for race conditions after sign-in)
    let token = null;
    let retries = 2; // Try up to 3 times (initial + 2 retries)
    let attempt = 0;
    
    while (!token && attempt <= retries) {
      if (attempt > 0) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      
      // Add a timeout to prevent hanging (reduced to 2 seconds)
      const sessionPromise = supabase.auth.getSession();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('getSession timeout')), 2000)
      );
      
      try {
        const result = await Promise.race([sessionPromise, timeoutPromise]);
        const { data, error } = result || { data: null, error: new Error('No result from getSession') };
        
        if (error) {
          console.warn(`⚠️  Error getting session (attempt ${attempt + 1}):`, error.message);
          attempt++;
          continue;
        }
        
        token = data?.session?.access_token || null;
        if (token) {
          // Cache the token with expiry (subtract 60 seconds for safety)
          const expiresAt = data.session.expires_at ? data.session.expires_at * 1000 : Date.now() + 3600000;
          cachedSessionToken = token;
          cachedSessionExpiry = expiresAt - 60000; // Cache until 1 minute before expiry
          break;
        } else {
          // No session at all (signed out) — don't retry, avoid log spam
          if (!data?.session) {
            cachedSessionToken = null;
            cachedSessionExpiry = null;
            break;
          }
          if (attempt < retries) {
            console.warn(`⚠️  No access token in session (attempt ${attempt + 1}/${retries + 1}). Retrying...`);
            attempt++;
            continue;
          } else {
            console.warn('⚠️  No access token in session after retries. Session exists:', !!data?.session);
            if (data?.session) {
              console.warn('   Session user:', data.session.user?.email);
              console.warn('   Session expires at:', data.session.expires_at);
            }
            cachedSessionToken = null;
            cachedSessionExpiry = null;
          }
        }
      } catch (raceError) {
        if (raceError.message === 'getSession timeout') {
          console.warn(`⚠️  getSession timed out (attempt ${attempt + 1})`);
        } else {
          console.warn(`⚠️  Error in getSession (attempt ${attempt + 1}):`, raceError.message);
        }
        attempt++;
        if (attempt > retries) {
          console.error('❌ getSession failed after all retries');
        }
      }
    }
    
    return token;
  } catch (error) {
    if (error.message === 'getSession timeout') {
      console.error('❌ getSession timed out after 2 seconds');
    } else {
      console.warn('⚠️  Error getting auth token:', error.message);
    }
    return null;
  }
}

/**
 * Update the cached session token (called from AuthContext when session changes)
 * @param {string|null} token - The access token to cache
 * @param {number|null} expiresAt - The expiry timestamp in milliseconds
 */
export function updateCachedSessionToken(token, expiresAt) {
  cachedSessionToken = token;
  cachedSessionExpiry = expiresAt ? expiresAt - 60000 : null; // Cache until 1 minute before expiry
  if (token) {
    console.log('✅ Cached session token updated');
  } else {
    console.log('🗑️  Cached session token cleared');
  }
}

/**
 * Create headers for API requests with authentication
 * @returns {Promise<Object>} Headers object with Authorization if token available
 */
export async function getAuthHeaders() {
  const headers = {
    'Content-Type': 'application/json',
  };
  
  const token = await getAuthToken();
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    console.warn('⚠️  No token available, request will be unauthenticated');
  }
  
  return headers;
}

/**
 * Clear authentication and redirect to login
 * Called when we detect invalid/expired tokens
 */
async function clearAuthAndRedirect() {
  console.warn('🔒 Invalid or expired authentication token detected. Clearing session and redirecting to login...');
  try {
    await supabase.auth.signOut();
    console.log('✅ Signed out from Supabase');
    
    // Clear any local storage that might contain tokens
    try {
      localStorage.removeItem('sb-auth-token');
      sessionStorage.clear();
    } catch (e) {
      console.warn('Could not clear storage:', e);
    }
    
    // Redirect to login page (use getBasePath for proper routing)
    const basePath = getBasePath();
    const loginPath = `${basePath}/login`;
    const currentPath = window.location.pathname;
    
    if (currentPath !== loginPath && !currentPath.includes('/auth/')) {
      window.location.href = loginPath;
    }
  } catch (error) {
    console.error('❌ Error clearing auth:', error);
    // Force redirect even if sign out fails
    const basePath = getBasePath();
    const loginPath = `${basePath}/login`;
    const currentPath = window.location.pathname;
    
    if (currentPath !== loginPath && !currentPath.includes('/auth/')) {
      window.location.href = loginPath;
    }
  }
}

/**
 * Authenticated fetch wrapper that automatically adds JWT token
 * Automatically handles 401 responses by clearing auth and redirecting to login
 * @param {string} url - URL to fetch
 * @param {Object} options - Fetch options (headers will be merged)
 * @returns {Promise<Response>} Fetch response
 */
export async function authenticatedFetch(url, options = {}) {
  const headers = await getAuthHeaders();
  const mergedHeaders = {
    ...headers,
    ...(options.headers || {})
  };

  const response = await fetch(url, {
    ...options,
    headers: mergedHeaders
  });

  // If we get a 401, the token is invalid/expired - clear auth and redirect
  if (response.status === 401) {
    console.warn('⚠️  Received 401 Unauthorized response. Token may be invalid or expired.');
    // Don't await - let it happen in background while we return the response
    clearAuthAndRedirect().catch(err => console.error('Error in clearAuthAndRedirect:', err));
  }

  return response;
}

/**
 * Fetch current user from the API server
 * @returns {Promise<Object>} User object with email, accountType, and darkMode
 */
export const fetchCurrentUser = async () => {
  try {
    const url = `${API_BASE_URL}/casc/v1/api/auth/current`;
    console.log('📤 Fetching current user from:', url);
    const headers = await getAuthHeaders();
    console.log('🔑 Headers:', JSON.stringify(headers, null, 2));
    console.log('🔑 Has Authorization header:', !!headers['Authorization']);
    
    // Add timeout to prevent hanging
    const controller = new AbortController();
    let timeoutId;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        console.error('⏱️  Request timeout after 8 seconds - aborting request');
        controller.abort();
        reject(new Error('Backend API timeout - request took longer than 8 seconds'));
      }, 8000); // 8 second timeout
    });
    
    console.log('🚀 Making fetch request...');
    const fetchStartTime = Date.now();
    
    // Race between fetch and timeout
    const response = await Promise.race([
      fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({}),
        signal: controller.signal
      }),
      timeoutPromise
    ]);
    
    clearTimeout(timeoutId);
    console.log('✅ Fetch completed in', Date.now() - fetchStartTime, 'ms, status:', response.status);

    if (!response.ok) {
      if (response.status === 401) {
        console.warn('⚠️  401 Unauthorized when fetching current user. Token is invalid or expired.');
        return { user: null, errorType: 'AUTH_FAILED' };
      }
      if (response.status >= 500 || response.status === 404 || response.status === 0) {
        console.warn('Backend unavailable (status ' + response.status + ').');
        return { user: null, errorType: 'SERVER_UNAVAILABLE' };
      }
      throw new Error(`Failed to fetch current user: ${response.statusText}`);
    }

    const userData = await response.json();
    console.log('📥 Raw response from server:', userData);

    if (!userData || !userData.email) {
      console.warn('⚠️  Server response missing email:', userData);
      return { user: null, errorType: 'SERVER_UNAVAILABLE' };
    }

    console.log('✅ Returning user data with email:', userData.email);
    return { user: userData, errorType: null };
  } catch (error) {
    const isTimeout = error.name === 'AbortError' || (error.message && error.message.toLowerCase().includes('timeout'));
    console.warn('Error fetching current user:', error.message);
    return { user: null, errorType: isTimeout ? 'TIMEOUT' : 'SERVER_UNAVAILABLE' };
  }
};

/**
 * Check if the backend server is reachable (no auth required).
 * @returns {Promise<boolean>} true if server responds, false otherwise
 */
export async function checkServerHealth() {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    const response = await fetch(`${API_BASE_URL}/casc/v1/api/auth/test`, {
      method: 'GET',
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response.ok;
  } catch {
    return false;
  }
};

/**
 * Fetch active AI runtime mode from server health.
 * @returns {Promise<{runtime: 'langchain'|'aisdk', healthy: boolean|null, status: string}>}
 */
export const fetchAiRuntimeStatus = async () => {
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/health`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch AI runtime status: ${response.statusText}`);
  }

  const payload = await response.json().catch(() => ({}));
  const runtimeRaw = String(payload?.checks?.services?.aiRuntime || '').trim().toLowerCase();
  const runtime = runtimeRaw === 'langchain' ? 'langchain' : 'aisdk';

  return {
    runtime,
    healthy: typeof payload?.healthy === 'boolean' ? payload.healthy : null,
    status: typeof payload?.status === 'string' ? payload.status : 'unknown'
  };
};

export const listAiProviders = async ({ enabledOnly = false } = {}) => {
  const headers = await getAuthHeaders();
  const qs = enabledOnly ? '?enabledOnly=true' : '';
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/ai/providers${qs}`, {
    method: 'GET',
    headers,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error || payload?.message || `Failed to load AI providers: ${response.statusText}`);
  }
  return { success: true, providers: Array.isArray(payload?.providers) ? payload.providers : [] };
};

export const listAiCatalogProviders = async ({ includePresets = true, refresh = false } = {}) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  if (includePresets !== undefined) params.set('includePresets', includePresets ? 'true' : 'false');
  if (refresh) params.set('refresh', 'true');
  const suffix = params.toString() ? `?${params.toString()}` : '';

  const response = await fetch(`${API_BASE_URL}/casc/v1/api/ai/catalog/providers${suffix}`, {
    method: 'GET',
    headers,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error || payload?.message || `Failed to load AI catalog providers: ${response.statusText}`);
  }
  return { success: true, providers: Array.isArray(payload?.providers) ? payload.providers : [] };
};

export const listAiCatalogModels = async (options = {}) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();

  const fields = [
    'provider',
    'providerApiType',
    'runtimeService',
    'modelType',
    'q',
    'embeddingOnly',
    'imageInput',
    'toolCall',
    'reasoning',
    'structuredOutput',
    'limit',
    'refresh',
  ];

  fields.forEach((key) => {
    if (options[key] === undefined || options[key] === null || options[key] === '') return;
    params.set(key, String(options[key]));
  });

  const suffix = params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/ai/catalog/models${suffix}`, {
    method: 'GET',
    headers,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error || payload?.message || `Failed to load AI catalog models: ${response.statusText}`);
  }
  return { success: true, models: Array.isArray(payload?.models) ? payload.models : [] };
};

export const createAiProvider = async (provider) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/ai/providers`, {
    method: 'POST',
    headers,
    body: JSON.stringify(provider || {}),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error || payload?.message || `Failed to create AI provider: ${response.statusText}`);
  }
  return { success: true, provider: payload.provider };
};

export const updateAiProvider = async (providerId, patch) => {
  if (!providerId) throw new Error('providerId is required');
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/ai/providers/${encodeURIComponent(providerId)}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(patch || {}),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error || payload?.message || `Failed to update AI provider: ${response.statusText}`);
  }
  return { success: true, provider: payload.provider };
};

export const listAiProviderModels = async ({ providerId, enabledOnly = false } = {}) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  if (providerId) params.set('providerId', providerId);
  if (enabledOnly) params.set('enabledOnly', 'true');
  const suffix = params.toString() ? `?${params.toString()}` : '';

  const response = await fetch(`${API_BASE_URL}/casc/v1/api/ai/provider-models${suffix}`, {
    method: 'GET',
    headers,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error || payload?.message || `Failed to load AI provider models: ${response.statusText}`);
  }
  return { success: true, models: Array.isArray(payload?.models) ? payload.models : [] };
};

export const createAiProviderModel = async (providerId, model) => {
  if (!providerId) throw new Error('providerId is required');
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/ai/providers/${encodeURIComponent(providerId)}/models`, {
    method: 'POST',
    headers,
    body: JSON.stringify(model || {}),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error || payload?.message || `Failed to create AI provider model: ${response.statusText}`);
  }
  return { success: true, model: payload.model };
};

export const updateAiProviderModel = async (modelId, patch) => {
  if (!modelId) throw new Error('modelId is required');
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/ai/provider-models/${encodeURIComponent(modelId)}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(patch || {}),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error || payload?.message || `Failed to update AI provider model: ${response.statusText}`);
  }
  return { success: true, model: payload.model };
};

export const deleteAiProviderModel = async (modelId) => {
  if (!modelId) throw new Error('modelId is required');
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/ai/provider-models/${encodeURIComponent(modelId)}`, {
    method: 'DELETE',
    headers,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error || payload?.message || `Failed to delete AI provider model: ${response.statusText}`);
  }
  return { success: true };
};

export const discoverAiProviderModels = async (providerId, apiKey) => {
  if (!providerId) throw new Error('providerId is required');
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/ai/providers/${encodeURIComponent(providerId)}/discover-models`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ apiKey }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error || payload?.message || `Failed to discover provider models: ${response.statusText}`);
  }
  return { success: true, models: Array.isArray(payload?.models) ? payload.models : [] };
};

export const pingAiProviderReachability = async (baseUrl) => {
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/ai/providers/reachability`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ baseUrl }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error || payload?.message || `Failed reachability check: ${response.statusText}`);
  }
  return payload;
};

export const listUserAiProviderKeys = async ({ includeDisabledProviders = false } = {}) => {
  const headers = await getAuthHeaders();
  const qs = includeDisabledProviders ? '?includeDisabledProviders=true' : '';
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/ai/user-provider-keys${qs}`, {
    method: 'GET',
    headers,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error || payload?.message || `Failed to load user provider keys: ${response.statusText}`);
  }

  return { success: true, keys: Array.isArray(payload?.keys) ? payload.keys : [] };
};

export const listUserActiveModelOptions = async () => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/ai/user/active-model-options`, {
    method: 'GET',
    headers,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error || payload?.message || `Failed to load active model options: ${response.statusText}`);
  }

  return {
    success: true,
    groups: Array.isArray(payload?.groups) ? payload.groups : [],
  };
};

export const listUserActiveModelOptionsForProvider = async (providerSlug) => {
  const slug = String(providerSlug || '').trim().toLowerCase();
  if (!slug) throw new Error('providerSlug is required');

  const headers = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE_URL}/casc/v1/api/ai/user/active-model-options/${encodeURIComponent(slug)}/models`,
    { method: 'GET', headers }
  );

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error || payload?.message || `Failed to load models for ${slug}: ${response.statusText}`);
  }

  return {
    success: true,
    provider: payload?.provider || null,
    models: Array.isArray(payload?.models) ? payload.models : [],
  };
};

export const saveUserAiProviderKey = async (providerId, apiKey, metadata) => {
  if (!providerId) throw new Error('providerId is required');

  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/ai/user-provider-keys/${encodeURIComponent(providerId)}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ apiKey, metadata }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error || payload?.message || `Failed to save provider key: ${response.statusText}`);
  }

  return { success: true, key: payload?.key || null };
};

export const deleteUserAiProviderKey = async (providerId) => {
  if (!providerId) throw new Error('providerId is required');

  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/ai/user-provider-keys/${encodeURIComponent(providerId)}`, {
    method: 'DELETE',
    headers,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error || payload?.message || `Failed to delete provider key: ${response.statusText}`);
  }

  return { success: true };
};

export const validateUserAiProviderKey = async ({ providerId, providerSlug, apiKey }) => {
  if (!providerId && !providerSlug) throw new Error('providerId or providerSlug is required');

  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/ai/user-provider-keys/validate`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ providerId, providerSlug, apiKey }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error || payload?.message || `Failed to validate provider key: ${response.statusText}`);
  }

return {
    success: true,
    status: payload?.status || 'error',
    valid: !!payload?.valid,
    message: payload?.message || '',
    error: payload?.error || '',
    httpStatus: payload?.httpStatus || null
  };
};

export const listUserAiProviderTestModels = async ({ providerId, providerSlug }) => {
  const providerRef = providerId || providerSlug;
  if (!providerRef) throw new Error('providerId or providerSlug is required');

  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/ai/user-provider-keys/${encodeURIComponent(providerRef)}/test-models`, {
    method: 'GET',
    headers,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error || payload?.message || `Failed to load provider test models: ${response.statusText}`);
  }

  return {
    success: true,
    provider: payload?.provider || null,
    models: Array.isArray(payload?.models) ? payload.models : []
  };
};

export const testUserAiProviderKeyWithModel = async ({ providerId, providerSlug, modelId, apiKey }) => {
  if (!providerId && !providerSlug) throw new Error('providerId or providerSlug is required');
  if (!modelId) throw new Error('modelId is required');

  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/ai/user-provider-keys/validate`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ providerId, providerSlug, modelId, apiKey }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error || payload?.message || `Failed to validate provider key: ${response.statusText}`);
  }

  return {
    success: true,
    status: payload?.status || 'error',
    valid: !!payload?.valid,
    message: payload?.message || '',
    error: payload?.error || '',
    httpStatus: payload?.httpStatus || null
  };
};

export const deleteAiProvider = async (providerId) => {
  if (!providerId) throw new Error('providerId is required');
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/ai/providers/${encodeURIComponent(providerId)}`, {
    method: 'DELETE',
    headers,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error || payload?.message || `Failed to delete AI provider: ${response.statusText}`);
  }
  return { success: true };
};

/**
 * Login user
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} User object
 */
export const loginUser = async (email, password) => {
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    throw new Error(`Failed to login: ${response.statusText}`);
  }

  return await response.json();
};

/**
 * Logout user
 * @returns {Promise<Object>} Logout response
 */
export const logoutUser = async () => {
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/auth/logout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    throw new Error(`Failed to logout: ${response.statusText}`);
  }

  return await response.json();
};

/**
 * Update user dark mode preference
 * @param {string} email - User email
 * @param {boolean} darkMode - Dark mode preference
 * @returns {Promise<Object>} Updated user settings
 */
export const updateUserDarkMode = async (email, darkMode) => {
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/user/settings`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, darkMode }),
  });

  if (!response.ok) {
    throw new Error(`Failed to update dark mode: ${response.statusText}`);
  }

  return await response.json();
};

export const updateUserChatMarkdown = async (email, chatMarkdownEnabled) => {
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/user/settings`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, chatMarkdownEnabled }),
  });

  if (!response.ok) {
    throw new Error(`Failed to update chat markdown preference: ${response.statusText}`);
  }

  return await response.json();
};

const apiUtils = {
  fetchSettings,
  saveSettings,
  fetchCurrentUser,
  fetchAiRuntimeStatus,
  listAiProviders,
  createAiProvider,
  updateAiProvider,
  listAiProviderModels,
  createAiProviderModel,
  updateAiProviderModel,
  deleteAiProviderModel,
  discoverAiProviderModels,
  pingAiProviderReachability,
  listUserAiProviderKeys,
  listUserActiveModelOptions,
  listUserActiveModelOptionsForProvider,
  saveUserAiProviderKey,
  deleteUserAiProviderKey,
  validateUserAiProviderKey,
  listUserAiProviderTestModels,
  testUserAiProviderKeyWithModel,
  loginUser,
  logoutUser,
  updateUserDarkMode,
  updateUserChatMarkdown,
  API_BASE_URL,
};

export default apiUtils;

// Prompts API helpers
/**
 * Get a prompt by id
 * @param {string} id
 */
export const getPromptById = async (id) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/prompts/${encodeURIComponent(id)}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({}),
  });
  if (resp.status === 404) {
    return { notFound: true };
  }
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    const msg = json?.message || json?.error || 'Failed to load prompt';
    throw new Error(msg);
  }
  return { prompt: json.prompt };
};

/**
 * Create a prompt
 * @param {Object} data
 */
export const createPrompt = async (data) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/prompts/create`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    const msg = json?.message || json?.error || 'Failed to create prompt';
    throw new Error(msg);
  }
  return json.prompt;
};

/**
 * Update a prompt by id
 * @param {string} id
 * @param {Object} data
 */
export const updatePrompt = async (id, data) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/prompts/update/${encodeURIComponent(id)}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    const msg = json?.message || json?.error || 'Failed to update prompt';
    throw new Error(msg);
  }
  return json.prompt;
};

// also export via default object for convenience
apiUtils.getPromptById = getPromptById;
apiUtils.createPrompt = createPrompt;
apiUtils.updatePrompt = updatePrompt;

/**
 * Ordered catalog prompts linked to a project for project chat workflow buttons.
 */
export const listProjectPromptButtons = async (projectId) => {
  if (!projectId) throw new Error('projectId is required');
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/project/prompt-buttons/list`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ projectId }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    const msg = json?.message || json?.error || 'Failed to load project workflow prompts';
    throw new Error(msg);
  }
  return Array.isArray(json.prompts) ? json.prompts : [];
};

/**
 * Replace workflow-button assignments for a project (ordered prompt ids from the project_chat_catalog).
 */
export const setProjectPromptButtons = async (projectId, promptIds) => {
  if (!projectId) throw new Error('projectId is required');
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/project/prompt-buttons/set`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ projectId, prompt_ids: Array.isArray(promptIds) ? promptIds : [] }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    const msg = json?.message || json?.error || 'Failed to save project workflow prompts';
    throw new Error(msg);
  }
  return json.data || json;
};

apiUtils.listProjectPromptButtons = listProjectPromptButtons;
apiUtils.setProjectPromptButtons = setProjectPromptButtons;

export const getProjectUserSummary = async (projectId) => {
  if (!projectId) throw new Error('projectId is required');
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/project/summary/get`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ projectId }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.message || json?.error || 'Failed to load project summary');
  }
  return json.data;
};

export const saveProjectUserSummary = async (projectId, { content, max_context_tokens, is_public }) => {
  if (!projectId) throw new Error('projectId is required');
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/project/summary/save`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      projectId,
      content: content ?? '',
      max_context_tokens: max_context_tokens,
      is_public: is_public,
    }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.message || json?.error || 'Failed to save project summary');
  }
  return json.data;
};

export const submitProjectSummaryToModel = async (projectId, sessionId) => {
  if (!projectId) throw new Error('projectId is required');
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/project/summary/submit-to-model`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ projectId, sessionId: sessionId || null }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.message || json?.error || 'Failed to submit summary to model context');
  }
  return json.data;
};

/**
 * Compress the current chat session into the user's project summary (manual).
 * @returns {{ summary, notice, meta }}
 */
export const updateProjectSummaryFromSession = async (projectId, sessionId) => {
  if (!projectId) throw new Error('projectId is required');
  if (!sessionId) throw new Error('sessionId is required');
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/project/summary/update-from-session`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ projectId, sessionId }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.message || json?.error || 'Failed to update project summary from chat');
  }
  return json.data;
};

apiUtils.getProjectUserSummary = getProjectUserSummary;
apiUtils.saveProjectUserSummary = saveProjectUserSummary;
apiUtils.submitProjectSummaryToModel = submitProjectSummaryToModel;
apiUtils.updateProjectSummaryFromSession = updateProjectSummaryFromSession;

// Ingestion API helper
/**
 * Trigger ingestion run for a specific group and suffix based on its saved config
 * Backend route: POST /casc/v1/api/ingestion/suffixes/{suffix}/groups/{groupSlug}/ingest
 * Expected response: { success: true, jobId: "<uuid>" }
 * @param {string} suffix
 * @param {string} groupSlug
 * @param {object} [options] - { start_row, total_count, validate_links, add_directory_depth_columns }
 */
export const ingestGroup = async (suffix, groupSlug, options) => {
  if (!suffix || !groupSlug) throw new Error('suffix and groupSlug are required');
  const url = `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(suffix)}/groups/${encodeURIComponent(groupSlug)}/ingest`;
  const headers = await getAuthHeaders();
  headers['Content-Type'] = 'application/json';
  const body = {};
  if (options) {
    if (typeof options.startRow === 'number') body.start_row = options.startRow;
    if (typeof options.totalCount === 'number') body.total_count = options.totalCount;
    if (options.validateLinks) body.validate_links = true;
    if (options.addDirectoryDepthColumns) body.add_directory_depth_columns = true;
    if (options.apiParamOverrides && typeof options.apiParamOverrides === 'object') {
      body.api_param_overrides = options.apiParamOverrides;
    }
    if (options.promptId) body.prompt_id = options.promptId;
  }
  const resp = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    const msg = json?.message || json?.error || 'Failed to start ingestion';
    const err = new Error(msg);
    err.status = resp.status;
    throw err;
  }
  const jobId = json?.jobId || json?.data?.jobId; // tolerate legacy shape if any
  if (!jobId) {
    throw new Error('Ingestion started but no jobId was returned');
  }
  return { jobId };
};

/**
 * Preview a transform pipeline without writing entries.
 * POST .../groups/{groupSlug}/transform/preview
 */
export const previewTransform = async (suffix, groupSlug, options = {}) => {
  if (!suffix || !groupSlug) throw new Error('suffix and groupSlug are required');
  const url = `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(suffix)}/groups/${encodeURIComponent(groupSlug)}/transform/preview`;
  const headers = await getAuthHeaders();
  headers['Content-Type'] = 'application/json';
  const body = {};
  if (options.config && typeof options.config === 'object') body.config = options.config;
  if (options.limit != null) body.limit = options.limit;
  const resp = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    const msg = json?.message || json?.error || 'Transform preview failed';
    const err = new Error(msg);
    err.status = resp.status;
    throw err;
  }
  return json.data || {};
};

apiUtils.previewTransform = previewTransform;

apiUtils.ingestGroup = ingestGroup;

/**
 * Poll ingestion progress
 * Backend route: GET /casc/v1/api/ingestion/suffixes/{suffix}/groups/{groupSlug}/ingest/{jobId}/progress
 * Returns: { success: true, data: { id, status, processed, total, percent, startedAt, finishedAt, error } }
 */
export const getIngestionProgress = async (suffix, groupSlug, jobId) => {
  if (!suffix || !groupSlug || !jobId) throw new Error('suffix, groupSlug and jobId are required');
  const url = `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(suffix)}/groups/${encodeURIComponent(groupSlug)}/ingest/${encodeURIComponent(jobId)}/progress`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'GET', headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    const msg = json?.message || json?.error || 'Failed to fetch ingestion progress';
    const err = new Error(msg);
    err.status = resp.status;
    throw err;
  }
  return json?.data || null;
};

apiUtils.getIngestionProgress = getIngestionProgress;

/**
 * Delete an ingestion group with cascade on server side
 * Backend route: DELETE /casc/v1/api/ingestion/suffixes/{suffix}/groups/{groupSlug}
 * Returns: { success: true, data: { memberships, entriesDeleted } }
 */
export const deleteIngestionGroupCascade = async (suffix, groupSlug) => {
  if (!suffix || !groupSlug) throw new Error('suffix and groupSlug are required');
  const url = `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(suffix)}/groups/${encodeURIComponent(groupSlug)}`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'DELETE', headers });
  let json = {};
  try { json = await resp.json(); } catch (_) { /* no body */ }
  if (!resp.ok) {
    const msg = json?.message || json?.error || `Failed to delete ingestion group: ${resp.statusText || 'Error'}`;
    const err = new Error(msg);
    err.status = resp.status;
    throw err;
  }
  if (!json?.success) {
    const err = new Error(json?.message || json?.error || 'Deletion failed');
    err.status = resp.status || 400;
    throw err;
  }
  const data = json?.data || {};
  return {
    memberships: typeof data.memberships === 'number' ? data.memberships : 0,
    entriesDeleted: typeof data.entriesDeleted === 'number' ? data.entriesDeleted : 0,
  };
};

apiUtils.deleteIngestionGroupCascade = deleteIngestionGroupCascade;

/**
 * Wipe ingested entries for a group (keeps the source). Optionally reset dynamic schema.
 * POST /casc/v1/api/ingestion/suffixes/{suffix}/groups/{groupSlug}/wipe
 * Body: { wipe_schema?: boolean }
 */
export const wipeIngestionGroupData = async (suffix, groupSlug, options = {}) => {
  if (!suffix || !groupSlug) throw new Error('suffix and groupSlug are required');
  const url = `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(suffix)}/groups/${encodeURIComponent(groupSlug)}/wipe`;
  const headers = await getAuthHeaders();
  headers['Content-Type'] = 'application/json';
  const resp = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      wipe_schema: Boolean(options.wipeSchema),
    }),
  });
  let json = {};
  try { json = await resp.json(); } catch (_) { /* no body */ }
  if (!resp.ok || !json?.success) {
    const msg = json?.message || json?.error || `Failed to wipe ingestion data: ${resp.statusText || 'Error'}`;
    const err = new Error(msg);
    err.status = resp.status;
    throw err;
  }
  return json?.data || {};
};

apiUtils.wipeIngestionGroupData = wipeIngestionGroupData;

// Google Spreadsheet source metadata helpers
/**
 * Get spreadsheet meta (title and tabs)
 */
export const gsGetMeta = async (spreadsheetId) => {
  if (!spreadsheetId) throw new Error('spreadsheetId required');
  const url = `${API_BASE_URL}/casc/v1/api/ingestion/source/google_spreadsheet/${encodeURIComponent(spreadsheetId)}/meta`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'GET', headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) throw new Error(json?.message || json?.error || 'Failed to fetch spreadsheet meta');
  return json.data || { title: '', tabs: [] };
};

/**
 * Get list of tabs (sheet names)
 */
export const gsGetTabs = async (spreadsheetId) => {
  if (!spreadsheetId) throw new Error('spreadsheetId required');
  const url = `${API_BASE_URL}/casc/v1/api/ingestion/source/google_spreadsheet/${encodeURIComponent(spreadsheetId)}/tabs`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'GET', headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) throw new Error(json?.message || json?.error || 'Failed to fetch spreadsheet tabs');
  return Array.isArray(json.data) ? json.data : [];
};

/**
 * Get list of columns for a tab. If sheet is omitted, server uses first tab.
 */
export const gsGetColumns = async (spreadsheetId, sheetName = '') => {
  if (!spreadsheetId) throw new Error('spreadsheetId required');
  const params = new URLSearchParams();
  if (sheetName) params.set('sheet', sheetName);
  const url = `${API_BASE_URL}/casc/v1/api/ingestion/source/google_spreadsheet/${encodeURIComponent(spreadsheetId)}/columns${params.toString() ? `?${params.toString()}` : ''}`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'GET', headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) throw new Error(json?.message || json?.error || 'Failed to fetch columns');
  const data = json.data || {};
  return { sheet_name: data.sheet_name || sheetName || '', columns: Array.isArray(data.columns) ? data.columns : [] };
};

apiUtils.gsGetMeta = gsGetMeta;
apiUtils.gsGetTabs = gsGetTabs;
apiUtils.gsGetColumns = gsGetColumns;

// Vector Tools API helpers
/**
 * Get a paginated list of vector tools
 * @param {number} page - 1-based page index
 * @param {number} pageSize - items per page
 * @returns {Promise<{items: Array, page: number, pageSize: number, total: number, totalPages: number}>}
 */
export const listVectorTools = async (page = 1, pageSize = 50, group = undefined, suffix = undefined) => {
  const params = new URLSearchParams();
  if (page) params.set('page', String(page));
  if (pageSize) params.set('pageSize', String(pageSize));
  if (group) params.set('group', String(group));
  if (suffix) params.set('suffix', String(suffix));
  // Vector Tools list endpoint per API docs
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/vector/tools?${params.toString()}`, {
    method: 'GET',
    headers,
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const msg = json?.message || json?.error || 'Failed to load tools';
    throw new Error(msg);
  }
  // Normalize safe shape
  const items = Array.isArray(json?.items) ? json.items : [];
  return {
    items,
    page: Number(json?.page) || page,
    pageSize: Number(json?.pageSize) || pageSize,
    total: Number(json?.total) || items.length,
    totalPages: Number(json?.totalPages) || 1,
  };
};

/**
 * Project chat global tool allowlist (horizon tools + active web search for current user).
 * @returns {Promise<{ ids: string[], slugs: string[] }>}
 */
export const fetchConversationIngestPlanes = async ({ suffix, projectId }) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  params.set('project_id', projectId);
  if (suffix) params.set('suffix', suffix);
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/conversation-ingest/planes?${params}`, { headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || 'Failed to load ingestion planes');
  }
  return json.data || {};
};

export const planConversationIngest = async ({
  suffix,
  projectId,
  messageId,
  sessionId,
  groupSlugs,
  field_selections,
  row_selections,
}) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/conversation-ingest/plan`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      suffix,
      project_id: projectId,
      message_id: messageId,
      session_id: sessionId,
      group_slugs: groupSlugs,
      field_selections,
      row_selections,
    }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || 'Failed to build ingestion plan from this message');
  }
  return json.data || {};
};

export const modifyConversationIngestPreview = async ({
  previewId,
  feedback,
  field_selections,
  row_selections,
}) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/conversation-ingest/modify`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      preview_id: previewId,
      feedback,
      field_selections,
      row_selections,
    }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || 'Failed to modify ingestion plan');
  }
  return json.data || {};
};

export const applyConversationIngestPreview = async ({ previewId, planes }) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/conversation-ingest/apply`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ preview_id: previewId, planes }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || 'Failed to apply ingestion');
  }
  return json.data || {};
};

export const discardConversationIngestPreview = async (previewId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/conversation-ingest/discard`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ preview_id: previewId }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || 'Failed to discard preview');
  }
  return json;
};

export const listProjectToolAllowlist = async () => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/vector/tools/project-allowlist`, {
    method: 'GET',
    headers,
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const msg = json?.message || json?.error || 'Failed to load project tool allowlist';
    throw new Error(msg);
  }
  const ids = Array.isArray(json?.ids) ? json.ids : [];
  const slugs = Array.isArray(json?.slugs) ? json.slugs : [];
  return { ids, slugs };
};

/**
 * Fetch one or more vector tools by ids
 * @param {string[]} ids
 * @returns {Promise<Array>} items
 */
export const getVectorToolsByIds = async (ids = [], group = undefined, suffix = undefined) => {
  const list = Array.isArray(ids) ? ids.filter(Boolean) : [];
  if (list.length === 0) return [];
  const params = new URLSearchParams();
  params.set('ids', list.join(','));
  if (group) params.set('group', String(group));
  if (suffix) params.set('suffix', String(suffix));
  // Vector Tools by-id endpoint per API docs (GET with ids query)
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/vector/tools/by-id?${params.toString()}`, {
    method: 'GET',
    headers,
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const msg = json?.message || json?.error || 'Failed to fetch tools by id';
    throw new Error(msg);
  }
  return Array.isArray(json?.items) ? json.items : [];
};

apiUtils.listVectorTools = listVectorTools;
apiUtils.listProjectToolAllowlist = listProjectToolAllowlist;
apiUtils.getVectorToolsByIds = getVectorToolsByIds;

// Embedding API helpers
/**
 * Request embedding for a tool
 * @param {string} toolId - Tool ID to create embedding request for
 * @returns {Promise<Object>} Embedding request response
 */
export const requestEmbedding = async (toolId, group) => {
  if (!group || typeof group !== 'string' || group.trim() === '') {
    throw new Error('Ingestion group is required to request embeddings');
  }
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/tools/embedding/request/${encodeURIComponent(toolId)}`, {
    method: 'POST',
    headers,
    // Server accepts any of these aliases; send all for safety
    body: JSON.stringify({ group, group_slug: group, ingestion_group_slug: group }),
  });

  const json = await response.json().catch(() => ({}));
  if (!response.ok || json?.success === false) {
    const msg = json?.message || json?.error || `Failed to request embedding: ${response.statusText}`;
    throw new Error(msg);
  }

  // Normalize to the shape expected by UI: { success, request }
  if (json && json.item) {
    // Return both normalized shape and spread fields for backward compatibility
    return { success: true, request: json.item, ...json.item };
  }
  // Fallback to existing server shape if already normalized
  if (json && json.request) return { success: json.success ?? true, ...json.request, ...json };
  return { success: true, request: json, ...json };
};

/**
 * Request embedding for product help documentation (zagger_product_help horizon).
 * @returns {Promise<Object>}
 */
export const requestProductHelpEmbedding = async () => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/help/embed`, {
    method: 'POST',
    headers,
  });

  const json = await response.json().catch(() => ({}));
  if (!response.ok || json?.success === false) {
    const msg = json?.message || json?.error || `Failed to request product help embedding: ${response.statusText}`;
    throw new Error(msg);
  }
  return json;
};

/**
 * Remove stored embeddings and embedding-queue rows for a vector search tool (same group as initiate embedding).
 * @param {string} toolId
 * @param {string} group - ingestion group slug
 */
export const removeEmbeddingsForTool = async (toolId, group) => {
  if (!group || typeof group !== 'string' || group.trim() === '') {
    throw new Error('Ingestion group is required to remove embeddings');
  }
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE_URL}/casc/v1/api/tools/embedding/${encodeURIComponent(toolId)}/remove-embeddings`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({ group, group_slug: group, ingestion_group_slug: group })
    }
  );

  const json = await response.json().catch(() => ({}));
  if (!response.ok || json?.success === false) {
    const msg = json?.message || json?.error || `Failed to remove embeddings: ${response.statusText}`;
    throw new Error(msg);
  }
  return json;
};

/**
 * Get embedding requests with optional status exclusion
 * @param {string[]} exclude - Optional array of statuses to exclude
 * @returns {Promise<Object>} Embedding requests response
 */
export const getEmbeddingRequests = async (exclude = null, group = undefined) => {
  const url = new URL(`${API_BASE_URL}/casc/v1/api/embedding`);
  if (exclude && Array.isArray(exclude) && exclude.length > 0) {
    exclude.forEach(status => url.searchParams.append('exclude', status));
  }
  if (group) {
    url.searchParams.set('group', String(group));
  }

  const headers = await getAuthHeaders();
  const response = await fetch(url.toString(), {
    method: 'GET',
    headers,
  });

  const json = await response.json().catch(() => ({}));
  if (!response.ok || json?.success === false) {
    const msg = json?.message || json?.error || `Failed to get embedding requests: ${response.statusText}`;
    throw new Error(msg);
  }

  // The backend returns a paginated response with { items: [...] }
  // Normalize to { success, requests: [...] }
  if (Array.isArray(json?.items)) {
    return { success: true, requests: json.items, page: json.page, pageSize: json.pageSize, total: json.total };
  }
  // If already in desired shape, pass through
  if (Array.isArray(json?.requests)) return json;
  // Fallback minimal normalization
  return { success: true, requests: [] };
};

/**
 * Get a single embedding request by ID
 * @param {string} requestId - Embedding request ID
 * @returns {Promise<Object>} Embedding request response
 */
export const getEmbeddingRequest = async (requestId) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/casc/v1/api/embedding/${encodeURIComponent(requestId)}`, {
    method: 'GET',
    headers,
  });

  const json = await response.json().catch(() => ({}));
  if (!response.ok || json?.success === false) {
    const msg = json?.message || json?.error || `Failed to get embedding request: ${response.statusText}`;
    throw new Error(msg);
  }

  // Normalize to { success, request }
  if (json && json.item) {
    return { success: true, request: json.item, ...json.item };
  }
  if (json && json.request) return { success: json.success ?? true, ...json.request, ...json };
  return { success: true, request: json, ...json };
};

// Add embedding functions to default export
apiUtils.requestEmbedding = requestEmbedding;
apiUtils.removeEmbeddingsForTool = removeEmbeddingsForTool;
apiUtils.getEmbeddingRequests = getEmbeddingRequests;
apiUtils.getEmbeddingRequest = getEmbeddingRequest;

/**
 * Get embedding coverage summary for a key/value without paging through all requests
 * New endpoint: POST /casc/v1/api/embedding/summary
 *
 * Accepted body forms (any one of):
 *  - { key: 'raw.title', value: 'Acme' }
 *  - { valuePath: 'raw.title', value: 'Acme' }
 *  - { field: 'raw.title', value: 'Acme' }
 *  - { raw: { title: 'Acme' } } // becomes key='raw.title', value='Acme'
 *
 * Normalized return shape:
 *  { success: true, total, embedded, lastEmbeddedAt }
 *
 * @param {Object|string} input - Either an object with any accepted alias fields, or a key string.
 * @param {string} [value] - Value to use when input is a key string or when forcing explicit key/value.
 */
export const getEmbeddingSummary = async (input, value = undefined) => {
  // Normalize input into a canonical { key, value } when possible.
  let body = {};
  let groupFromInput = undefined;
  try {
    if (typeof input === 'string') {
      if (typeof value === 'undefined') {
        throw new Error('getEmbeddingSummary requires a value when input is a key string');
      }
      body = { key: input, value };
    } else if (input && typeof input === 'object') {
      // Support alias fields; if multiple provided, prefer explicit key/value.
      if (input.key && typeof input.value !== 'undefined') {
        body = { key: input.key, value: input.value };
      } else if (input.valuePath && typeof input.value !== 'undefined') {
        body = { key: input.valuePath, value: input.value };
      } else if (input.field && typeof input.value !== 'undefined') {
        body = { key: input.field, value: input.value };
      } else if (input.raw && typeof input.raw === 'object' && input.raw !== null) {
        const keys = Object.keys(input.raw);
        if (keys.length > 0) {
          const k = keys[0];
          body = { key: `raw.${k}`, value: input.raw[k] };
        }
      }
      // Capture group value via any accepted alias
      groupFromInput = input.group || input.group_slug || input.ingestion_group_slug;
      // If we didn't manage to normalize, fall back to passing through the input as-is
      if (!body.key || typeof body.value === 'undefined') {
        body = { ...input };
      }
    } else {
      throw new Error('Invalid input for getEmbeddingSummary');
    }
  } catch (e) {
    // Re-throw with clearer message for callers
    throw new Error(e.message || 'Failed to prepare request for embedding summary');
  }

  // API requires an ingestion group as of 2025-12-31
  const group = groupFromInput;
  if (!group || typeof group !== 'string' || group.trim() === '') {
    throw new Error('Ingestion group is required for embedding summary');
  }
  // Ensure group aliases are present in the body for server-side convenience
  body.group = group;
  body.group_slug = group;
  body.ingestion_group_slug = group;

  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/embedding/summary`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.message || json?.error || 'Failed to fetch embedding summary';
    throw new Error(msg);
  }

  // Normalize response
  const result = {
    success: json.success ?? true,
    total: typeof json.total === 'number' ? json.total : 0,
    embedded: typeof json.embedded === 'number' ? json.embedded : 0,
    lastEmbeddedAt: json.lastEmbeddedAt || null,
    lastIngestionAt: json.lastIngestionAt || null,
    needsReembed: Boolean(json.needsReembed),
  };
  return result;
};

// expose via default export convenience object
apiUtils.getEmbeddingSummary = getEmbeddingSummary;

/**
 * Get embedding status for a specific entry/key within a required ingestion group
 * Endpoint: GET /casc/v1/api/embedding/status
 * @param {Object} params
 * @param {string} params.key - key or raw.<col>
 * @param {string} params.entry_id - entry uuid
 * @param {string} params.group - ingestion group (aliases accepted)
 * @returns {Promise<{success:boolean,status:Object}>}
 */
export const getEmbeddingStatus = async ({ key, entry_id, group, field, valuePath, group_slug, ingestion_group_slug } = {}) => {
  const finalKey = key || field || valuePath;
  const finalGroup = group || group_slug || ingestion_group_slug;
  if (!finalKey || typeof finalKey !== 'string') {
    throw new Error('key is required for embedding status');
  }
  if (!entry_id || typeof entry_id !== 'string') {
    throw new Error('entry_id is required for embedding status');
  }
  if (!finalGroup || typeof finalGroup !== 'string' || finalGroup.trim() === '') {
    throw new Error('Ingestion group is required for embedding status');
  }

  const url = new URL(`${API_BASE_URL}/casc/v1/api/embedding/status`);
  url.searchParams.set('key', finalKey);
  url.searchParams.set('entry_id', entry_id);
  // provide all aliases to be safe
  url.searchParams.set('group', finalGroup);
  url.searchParams.set('group_slug', finalGroup);
  url.searchParams.set('ingestion_group_slug', finalGroup);

  const headers = await getAuthHeaders();
  const resp = await fetch(url.toString(), { method: 'GET', headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.message || json?.error || 'Failed to fetch embedding status';
    throw new Error(msg);
  }
  // Normalize
  return {
    success: json.success ?? true,
    status: json.status || {
      reference_id: null,
      embedding_id: null,
      embedded_at: null,
      has_embedding: false,
      group_slug: finalGroup,
    },
  };
};

apiUtils.getEmbeddingStatus = getEmbeddingStatus;

// ── Periodic Ingestion Schedule API ──────────────────────────────────────

/**
 * Settings > Ingestion: one paginated load (groups + horizon links + schedule fields).
 * @param {number} [page=1]
 * @param {number} [pageSize=20] — server default 20 if invalid; no maximum.
 * @param {{ horizonScope?: 'all'|'active'|'unassigned', activeSuffix?: string, nameContains?: string }} [options]
 * @returns {Promise<{ success, horizons, items, total, page, pageSize, horizonScope?, nameContains? }>}
 */
export const fetchIngestionSettingsSourcesPage = async (page = 1, pageSize = 20, options = {}) => {
  const url = `${API_BASE_URL}/casc/v1/api/ingestion/settings/sources-page`;
  const headers = await getAuthHeaders();
  const body = { page, pageSize };
  if (options.horizonScope) body.horizonScope = options.horizonScope;
  if (options.activeSuffix != null && String(options.activeSuffix).trim() !== '') {
    body.activeSuffix = String(options.activeSuffix).trim();
  }
  if (options.nameContains != null && String(options.nameContains).trim() !== '') {
    body.nameContains = String(options.nameContains).trim();
  }
  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify(body),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to load ingestion sources';
    throw new Error(msg);
  }
  return json;
};
apiUtils.fetchIngestionSettingsSourcesPage = fetchIngestionSettingsSourcesPage;

// ── Postgres DB ingestion ────────────────────────────────────────────────

async function postgresIngestFetch(path, { method = 'GET', body } = {}) {
  const url = `${API_BASE_URL}/casc/v1/api${path}`;
  const headers = await getAuthHeaders();
  const opts = { method, headers: { ...headers } };
  if (body !== undefined) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }
  const resp = await fetch(url, opts);
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    throw new Error(json?.error || json?.message || `Request failed (${resp.status})`);
  }
  return json;
}

export const listPostgresConnections = (suffix) =>
  postgresIngestFetch(`/ingestion/suffixes/${encodeURIComponent(suffix)}/postgres/connections`);

export const createPostgresConnection = (suffix, payload) =>
  postgresIngestFetch(`/ingestion/suffixes/${encodeURIComponent(suffix)}/postgres/connections`, {
    method: 'POST',
    body: payload,
  });

export const updatePostgresConnection = (suffix, id, payload) =>
  postgresIngestFetch(`/ingestion/suffixes/${encodeURIComponent(suffix)}/postgres/connections/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: payload,
  });

export const testPostgresConnectionPayload = (suffix, payload) =>
  postgresIngestFetch(`/ingestion/suffixes/${encodeURIComponent(suffix)}/postgres/connections/test-payload`, {
    method: 'POST',
    body: payload,
  });

export const testPostgresConnection = (suffix, id) =>
  postgresIngestFetch(`/ingestion/suffixes/${encodeURIComponent(suffix)}/postgres/connections/${encodeURIComponent(id)}/test`, {
    method: 'POST',
    body: {},
  });

export const introspectPostgresConnection = (suffix, id, body = {}) =>
  postgresIngestFetch(`/ingestion/suffixes/${encodeURIComponent(suffix)}/postgres/connections/${encodeURIComponent(id)}/introspect`, {
    method: 'POST',
    body,
  });

export const proposePostgresPlanes = (suffix, id, body = {}) =>
  postgresIngestFetch(`/ingestion/suffixes/${encodeURIComponent(suffix)}/postgres/connections/${encodeURIComponent(id)}/propose`, {
    method: 'POST',
    body,
  });

export const setupPostgresPlanes = (suffix, id, body) =>
  postgresIngestFetch(`/ingestion/suffixes/${encodeURIComponent(suffix)}/postgres/connections/${encodeURIComponent(id)}/setup`, {
    method: 'POST',
    body,
  });

apiUtils.listPostgresConnections = listPostgresConnections;
apiUtils.createPostgresConnection = createPostgresConnection;
apiUtils.updatePostgresConnection = updatePostgresConnection;
apiUtils.testPostgresConnectionPayload = testPostgresConnectionPayload;
apiUtils.testPostgresConnection = testPostgresConnection;
apiUtils.introspectPostgresConnection = introspectPostgresConnection;
apiUtils.proposePostgresPlanes = proposePostgresPlanes;
apiUtils.setupPostgresPlanes = setupPostgresPlanes;

/**
 * Get schedule status for an ingestion group
 * @param {string} suffix
 * @param {string} groupSlug
 * @returns {Promise<Object>} { success, schedule }
 */
export const getScheduleStatus = async (suffix, groupSlug) => {
  if (!suffix || !groupSlug) throw new Error('suffix and groupSlug are required');
  const url = `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(suffix)}/groups/${encodeURIComponent(groupSlug)}/schedule`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'GET', headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to get schedule status';
    throw new Error(msg);
  }
  return json;
};
apiUtils.getScheduleStatus = getScheduleStatus;

/**
 * Enable or update schedule for an ingestion group
 * @param {string} suffix
 * @param {string} groupSlug
 * @param {Object} schedule - { period_type, period_value, start_time?, timezone? }
 * @returns {Promise<Object>} { success, message, next_ingestion_at }
 */
export const enableSchedule = async (suffix, groupSlug, schedule) => {
  if (!suffix || !groupSlug) throw new Error('suffix and groupSlug are required');
  const url = `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(suffix)}/groups/${encodeURIComponent(groupSlug)}/schedule`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'PUT', headers, body: JSON.stringify(schedule) });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to enable schedule';
    throw new Error(msg);
  }
  return json;
};
apiUtils.enableSchedule = enableSchedule;

/**
 * Disable schedule for an ingestion group
 * @param {string} suffix
 * @param {string} groupSlug
 * @returns {Promise<Object>}
 */
export const disableSchedule = async (suffix, groupSlug) => {
  if (!suffix || !groupSlug) throw new Error('suffix and groupSlug are required');
  const url = `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(suffix)}/groups/${encodeURIComponent(groupSlug)}/schedule`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'DELETE', headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to disable schedule';
    throw new Error(msg);
  }
  return json;
};
apiUtils.disableSchedule = disableSchedule;

/**
 * Pause schedule for an ingestion group
 * @param {string} suffix
 * @param {string} groupSlug
 * @returns {Promise<Object>}
 */
export const pauseSchedule = async (suffix, groupSlug) => {
  if (!suffix || !groupSlug) throw new Error('suffix and groupSlug are required');
  const url = `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(suffix)}/groups/${encodeURIComponent(groupSlug)}/schedule/pause`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'POST', headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to pause schedule';
    throw new Error(msg);
  }
  return json;
};
apiUtils.pauseSchedule = pauseSchedule;

/**
 * Resume schedule for an ingestion group
 * @param {string} suffix
 * @param {string} groupSlug
 * @returns {Promise<Object>}
 */
export const resumeSchedule = async (suffix, groupSlug) => {
  if (!suffix || !groupSlug) throw new Error('suffix and groupSlug are required');
  const url = `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(suffix)}/groups/${encodeURIComponent(groupSlug)}/schedule/resume`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'POST', headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to resume schedule';
    throw new Error(msg);
  }
  return json;
};
apiUtils.resumeSchedule = resumeSchedule;

/**
 * Manually trigger ingestion for a scheduled group
 * @param {string} suffix
 * @param {string} groupSlug
 * @returns {Promise<Object>}
 */
export const triggerScheduledIngestion = async (suffix, groupSlug) => {
  if (!suffix || !groupSlug) throw new Error('suffix and groupSlug are required');
  const url = `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(suffix)}/groups/${encodeURIComponent(groupSlug)}/schedule/trigger`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'POST', headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to trigger ingestion';
    throw new Error(msg);
  }
  return json;
};
apiUtils.triggerScheduledIngestion = triggerScheduledIngestion;

/**
 * Get all scheduled ingestion groups across all suffixes
 * @returns {Promise<Object>} { success, data: { [suffix]: [...groups] } }
 */
export const getAllScheduledGroups = async () => {
  const url = `${API_BASE_URL}/casc/v1/api/ingestion/schedules`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'GET', headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to get scheduled groups';
    throw new Error(msg);
  }
  return json;
};
apiUtils.getAllScheduledGroups = getAllScheduledGroups;

// ── Toolchain scheduler ───────────────────────────────────────────────────

async function schedulerFetch(path, { method = 'GET', body } = {}) {
  const url = `${API_BASE_URL}/casc/v1/api${path}`;
  const headers = await getAuthHeaders();
  const opts = { method, headers: { ...headers } };
  if (body !== undefined) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }
  const resp = await fetch(url, opts);
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    throw new Error(json?.error || json?.message || `Request failed (${resp.status})`);
  }
  return json;
}

export const listSchedulerJobs = (params = {}) => {
  const q = new URLSearchParams();
  if (params.suffix) q.set('suffix', params.suffix);
  const qs = q.toString();
  return schedulerFetch(`/scheduler/jobs${qs ? `?${qs}` : ''}`);
};
apiUtils.listSchedulerJobs = listSchedulerJobs;

export const getSchedulerJob = (suffix, toolSlug, { limit } = {}) => {
  const q = limit ? `?limit=${encodeURIComponent(limit)}` : '';
  return schedulerFetch(
    `/scheduler/jobs/${encodeURIComponent(suffix)}/${encodeURIComponent(toolSlug)}${q}`
  );
};
apiUtils.getSchedulerJob = getSchedulerJob;

export const getSchedulerErrorCount = (suffix) => {
  const q = suffix ? `?suffix=${encodeURIComponent(suffix)}` : '';
  return schedulerFetch(`/scheduler/error-count${q}`);
};
apiUtils.getSchedulerErrorCount = getSchedulerErrorCount;

export const activateSchedulerJob = (suffix, toolSlug) =>
  schedulerFetch(`/scheduler/jobs/${encodeURIComponent(suffix)}/${encodeURIComponent(toolSlug)}/activate`, {
    method: 'POST',
  });
apiUtils.activateSchedulerJob = activateSchedulerJob;

export const pauseSchedulerJob = (suffix, toolSlug) =>
  schedulerFetch(`/scheduler/jobs/${encodeURIComponent(suffix)}/${encodeURIComponent(toolSlug)}/pause`, {
    method: 'POST',
  });
apiUtils.pauseSchedulerJob = pauseSchedulerJob;

export const resumeSchedulerJob = (suffix, toolSlug) =>
  schedulerFetch(`/scheduler/jobs/${encodeURIComponent(suffix)}/${encodeURIComponent(toolSlug)}/resume`, {
    method: 'POST',
  });
apiUtils.resumeSchedulerJob = resumeSchedulerJob;

export const deactivateSchedulerJob = (suffix, toolSlug) =>
  schedulerFetch(`/scheduler/jobs/${encodeURIComponent(suffix)}/${encodeURIComponent(toolSlug)}/deactivate`, {
    method: 'POST',
  });
apiUtils.deactivateSchedulerJob = deactivateSchedulerJob;

export const clearSchedulerAttention = (suffix, toolSlug) =>
  schedulerFetch(
    `/scheduler/jobs/${encodeURIComponent(suffix)}/${encodeURIComponent(toolSlug)}/clear-attention`,
    { method: 'POST' }
  );
apiUtils.clearSchedulerAttention = clearSchedulerAttention;

export const triggerSchedulerJob = (suffix, toolSlug) =>
  schedulerFetch(`/scheduler/jobs/${encodeURIComponent(suffix)}/${encodeURIComponent(toolSlug)}/trigger`, {
    method: 'POST',
  });
apiUtils.triggerSchedulerJob = triggerSchedulerJob;

/**
 * Get ingestion notifications
 * @param {Object} params - { limit?, offset?, unacknowledged? }
 * @returns {Promise<Object>} { success, notifications }
 */
export const getIngestionNotifications = async ({ limit = 50, offset = 0, unacknowledged = false } = {}) => {
  const params = new URLSearchParams({ limit, offset });
  if (unacknowledged) params.set('unacknowledged', 'true');
  const url = `${API_BASE_URL}/casc/v1/api/ingestion/notifications?${params}`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'GET', headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to get notifications';
    throw new Error(msg);
  }
  return json;
};
apiUtils.getIngestionNotifications = getIngestionNotifications;

/**
 * Acknowledge an ingestion notification
 * @param {string} notificationId
 * @returns {Promise<Object>}
 */
export const acknowledgeNotification = async (notificationId) => {
  if (!notificationId) throw new Error('notificationId is required');
  const url = `${API_BASE_URL}/casc/v1/api/ingestion/notifications/${encodeURIComponent(notificationId)}/acknowledge`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'POST', headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to acknowledge notification';
    throw new Error(msg);
  }
  return json;
};
apiUtils.acknowledgeNotification = acknowledgeNotification;

/**
 * Test an API provider connection (system settings)
 * @param {string} provider - e.g. 'xe.net'
 * @returns {Promise<Object>}
 */
export const testApiProvider = async (provider = 'xe.net') => {
  const url = `${API_BASE_URL}/casc/v1/api/system-settings/test-api-provider`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'POST', headers, body: JSON.stringify({ provider }) });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'API provider test failed';
    throw new Error(msg);
  }
  return json;
};
apiUtils.testApiProvider = testApiProvider;

// ── Personal Google OAuth ──────────────────────────────────────

/**
 * Get user's personal Google OAuth status
 * @returns {Promise<Object>} { success, personal, system, activeSource, canAccessDrive }
 */
export const getUserGoogleAuthStatus = async () => {
  const url = `${API_BASE_URL}/casc/v1/api/user/google/auth/status`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'POST', headers, body: JSON.stringify({}) });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to get personal Google auth status';
    throw new Error(msg);
  }
  return json;
};
apiUtils.getUserGoogleAuthStatus = getUserGoogleAuthStatus;

/** When `isAuthenticated` but not `canAccessDrive`, API returns `driveAccessFailureReason`. */
export function driveAccessFailureHint(reason) {
  switch (reason) {
    case 'drive_api_disabled':
      return 'Enable the Google Drive API in the Google Cloud project that owns your OAuth client (APIs & Services → Library).';
    case 'token_invalid':
      return 'Token expired or revoked — in User Settings, disconnect Personal Google Drive and connect again.';
    case 'permission_denied':
      return 'Drive access was denied — disconnect and connect again so all requested scopes are granted.';
    default:
      return 'Drive could not be verified — check the OAuth project in Google Cloud or reconnect in User Settings.';
  }
}
apiUtils.driveAccessFailureHint = driveAccessFailureHint;

/**
 * Get personal Google OAuth consent URL
 * @returns {Promise<Object>} { success, authUrl }
 */
export const getUserGoogleAuthUrl = async () => {
  const url = `${API_BASE_URL}/casc/v1/api/user/google/auth/url`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'POST', headers, body: JSON.stringify({}) });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to generate personal Google auth URL';
    throw new Error(msg);
  }
  return json;
};
apiUtils.getUserGoogleAuthUrl = getUserGoogleAuthUrl;

/**
 * Disconnect personal Google OAuth
 * @returns {Promise<Object>} { success }
 */
export const disconnectUserGoogleAuth = async () => {
  const url = `${API_BASE_URL}/casc/v1/api/user/google/auth/disconnect`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'POST', headers, body: JSON.stringify({}) });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to disconnect personal Google auth';
    throw new Error(msg);
  }
  return json;
};
apiUtils.disconnectUserGoogleAuth = disconnectUserGoogleAuth;

/**
 * Get per-user Google OAuth app credentials (masked secret)
 * @returns {Promise<Object>} { success, credentials: { client_id, client_secret, redirect_uri, hasCredentials } }
 */
export const getUserGoogleCredentials = async () => {
  const url = `${API_BASE_URL}/casc/v1/api/user/google/auth/credentials`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'GET', headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to get personal Google OAuth credentials';
    throw new Error(msg);
  }
  return json;
};
apiUtils.getUserGoogleCredentials = getUserGoogleCredentials;

/**
 * Save per-user Google OAuth app credentials
 * @param {Object} creds - { client_id, client_secret, redirect_uri, keep_existing_secret }
 * @returns {Promise<Object>} { success }
 */
export const saveUserGoogleCredentials = async ({ client_id, client_secret, redirect_uri, keep_existing_secret }) => {
  const url = `${API_BASE_URL}/casc/v1/api/user/google/auth/credentials`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ client_id, client_secret, redirect_uri, keep_existing_secret })
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to save personal Google OAuth credentials';
    throw new Error(msg);
  }
  return json;
};
apiUtils.saveUserGoogleCredentials = saveUserGoogleCredentials;

/**
 * List public admin-managed Google OAuth providers visible to the signed-in user.
 * @returns {Promise<Object>} { success, providers: [...] }
 */
export const listUserGoogleCredentialImports = async () => {
  const url = `${API_BASE_URL}/casc/v1/api/user/google/auth/credential-imports`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'GET', headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to load importable Google OAuth credentials';
    throw new Error(msg);
  }
  return json;
};
apiUtils.listUserGoogleCredentialImports = listUserGoogleCredentialImports;

/**
 * Import one visible admin-managed Google OAuth provider into this user's credentials.
 * @param {string} providerId
 * @returns {Promise<Object>} { success, provider }
 */
export const importUserGoogleCredentials = async (providerId) => {
  const url = `${API_BASE_URL}/casc/v1/api/user/google/auth/credential-imports/import`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ providerId })
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to import Google OAuth credentials';
    throw new Error(msg);
  }
  return json;
};
apiUtils.importUserGoogleCredentials = importUserGoogleCredentials;

/**
 * Get admin-managed Google OAuth providers. Secrets are not returned.
 * @returns {Promise<Object>} { success, providers: [...] }
 */
export const getGoogleOAuthProviders = async () => {
  const url = `${API_BASE_URL}/casc/v1/api/admin/google-oauth-providers/get`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'POST', headers, body: JSON.stringify({}) });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to get Google OAuth providers';
    throw new Error(msg);
  }
  return json;
};
apiUtils.getGoogleOAuthProviders = getGoogleOAuthProviders;

/**
 * Save admin-managed Google OAuth providers. Blank client_secret preserves existing secret.
 * @param {Array<Object>} providers
 * @returns {Promise<Object>} { success, providers: [...] }
 */
export const saveGoogleOAuthProviders = async (providers) => {
  const url = `${API_BASE_URL}/casc/v1/api/admin/google-oauth-providers/set`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ providers })
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to save Google OAuth providers';
    throw new Error(msg);
  }
  return json;
};
apiUtils.saveGoogleOAuthProviders = saveGoogleOAuthProviders;

/**
 * Get AI test definitions from the backend.
 * @returns {Promise<Object>} { success, definitions: [...] }
 */
export const getAiTestDefinitions = async () => {
  const url = `${API_BASE_URL}/casc/v1/api/ai/tests/definitions`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'GET', headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to load test definitions';
    throw new Error(msg);
  }
  return json;
};

/**
 * Create and run an AI test suite.
 * @param {string} providerSlug - Provider slug (e.g., 'openai')
 * @param {string} modelId - Model ID (e.g., 'gpt-4o')
 * @param {Object} options - Optional run configuration
 * @param {string[]} [options.selectedTests] - Optional list of test slugs to execute
 * @param {Object} [options.testOverrides] - Optional per-test parameter overrides keyed by slug
 * @returns {Promise<Object>} { success, run: {...} }
 */
export const runAiTestSuite = async (providerSlug, modelId, options = {}) => {
  const url = `${API_BASE_URL}/casc/v1/api/ai/tests/run`;
  const headers = await getAuthHeaders();
  const selectedTests = Array.isArray(options?.selectedTests)
    ? options.selectedTests.map((slug) => String(slug || '').trim()).filter(Boolean)
    : undefined;
  const testOverrides = (options?.testOverrides && typeof options.testOverrides === 'object' && !Array.isArray(options.testOverrides))
    ? options.testOverrides
    : undefined;
  const resp = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      provider_slug: providerSlug,
      model_id: modelId,
      ...(selectedTests ? { selected_tests: selectedTests } : {}),
      ...(testOverrides ? { test_overrides: testOverrides } : {})
    })
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to run test suite';
    throw new Error(msg);
  }
  return json;
};

/**
 * Get AI test runs history.
 * @param {number} limit - Max runs to return (default 20)
 * @returns {Promise<Object>} { success, runs: [...] }
 */
export const getAiTestRuns = async (page = 1, limit = 10) => {
  const url = `${API_BASE_URL}/casc/v1/api/ai/tests/runs?page=${page}&limit=${limit}`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'GET', headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to load test runs';
    throw new Error(msg);
  }
  return json;
};

/**
 * Get a specific AI test run with results.
 * @param {string} runId - Run ID (UUID)
 * @returns {Promise<Object>} { success, run: {...}, results: [...] }
 */
export const getAiTestRun = async (runId) => {
  const url = `${API_BASE_URL}/casc/v1/api/ai/tests/run/${encodeURIComponent(runId)}`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'GET', headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const msg = json?.error || json?.message || 'Failed to load test run';
    throw new Error(msg);
  }
  return json;
};

/**
 * Run web search API test(s) using the current user's Web Search API keys.
 * @param {{ provider?: string, queries: string|string[], limit?: number }} options
 */
export const runWebSearchTest = async ({ provider = 'brave', queries, limit = 5 } = {}) => {
  const url = `${API_BASE_URL}/casc/v1/api/ai/tests/web-search`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ provider, queries, limit })
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const err = new Error(json?.error || json?.message || 'Web search test failed');
    if (json?.configured_keys || json?.key_expected) {
      err.details = JSON.stringify(
        {
          provider_requested: json.provider_requested,
          key_expected: json.key_expected,
          configured_keys: json.configured_keys || []
        },
        null,
        2
      );
    }
    throw err;
  }
  return json;
};

export const listMcpServers = async () => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/user/mcp-servers`, { headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    throw new Error(json?.error || json?.message || 'Failed to load MCP servers');
  }
  return json;
};

export const createMcpServer = async (payload) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/user/mcp-servers`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload)
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    throw new Error(json?.error || json?.message || 'Failed to create MCP server');
  }
  return json;
};

export const updateMcpServer = async (serverId, payload) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/user/mcp-servers/${encodeURIComponent(serverId)}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(payload)
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    throw new Error(json?.error || json?.message || 'Failed to update MCP server');
  }
  return json;
};

export const deleteMcpServer = async (serverId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/user/mcp-servers/${encodeURIComponent(serverId)}`, {
    method: 'DELETE',
    headers
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    throw new Error(json?.error || json?.message || 'Failed to delete MCP server');
  }
  return json;
};

export const testMcpServer = async (serverId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/user/mcp-servers/${encodeURIComponent(serverId)}/test`, {
    method: 'POST',
    headers
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    throw new Error(json?.error || json?.message || 'MCP test failed');
  }
  return json;
};

export const listMcpServerTools = async (serverId, { allowInactive = false } = {}) => {
  const headers = await getAuthHeaders();
  const qs = allowInactive ? '?allowInactive=1' : '';
  const resp = await fetch(
    `${API_BASE_URL}/casc/v1/api/user/mcp-servers/${encodeURIComponent(serverId)}/tools${qs}`,
    { headers }
  );
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    throw new Error(json?.error || json?.message || 'Failed to list MCP tools');
  }
  return json;
};

export const callMcpServerTool = async (serverId, toolName, args = {}) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(
    `${API_BASE_URL}/casc/v1/api/user/mcp-servers/${encodeURIComponent(serverId)}/call`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({ tool_name: toolName, arguments: args }),
    }
  );
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    throw new Error(json?.error || json?.message || 'MCP call failed');
  }
  return json;
};

export const runIngestionApiTest = async ({ groupSlug, paramOverrides, paramOverridesByGroup } = {}) => {
  const headers = await getAuthHeaders();
  const body = {};
  if (groupSlug) body.groupSlug = groupSlug;
  if (paramOverrides && typeof paramOverrides === 'object') body.paramOverrides = paramOverrides;
  if (paramOverridesByGroup && typeof paramOverridesByGroup === 'object') {
    body.paramOverridesByGroup = paramOverridesByGroup;
  }
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/ai/tests/ingestion-api`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    const err = new Error(json?.error || json?.message || 'Ingestion API harness failed');
    err.result = json;
    if (json?.steps) err.details = JSON.stringify(json.steps, null, 2);
    throw err;
  }
  return json;
};

export const runMcpTest = async ({ serverId, query } = {}) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/ai/tests/mcp`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ serverId, query })
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    throw new Error(json?.error || json?.message || 'MCP admin test failed');
  }
  return json;
};

/** Rename a chat session (POST /chat/session/rename). */
export const renameChatSession = async (sessionId, name) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/session/rename`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, name: String(name || '').trim() }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.message || json?.error || 'Failed to rename chat');
  }
  return json;
};

/** Read chat session.metadata (UI prefs, locked record, wait requests, etc.). */
export const getSessionMetadata = async (sessionId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/session/metadata/get`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.message || json?.error || 'Failed to load session metadata');
  }
  return json.data || {};
};

/** Merge allowlisted UI prefs into session.metadata. */
export const mergeSessionMetadata = async (sessionId, patch = {}) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/session/metadata/merge`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, patch }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.message || json?.error || 'Failed to update session metadata');
  }
  return json.data || {};
};

/** Get locked record / find-record request for a chat session. */
export const getSessionLockedRecord = async (sessionId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/session/locked-record/get`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.message || json?.error || 'Failed to load locked record');
  }
  return json.data || {};
};

/** Lock a Horizon entry to the chat session. */
export const setSessionLockedRecord = async ({ sessionId, group_slug, entry_id, label = null }) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/session/locked-record/set`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, group_slug, entry_id, label }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.message || json?.error || 'Failed to lock record');
  }
  return json.data || {};
};

/** Clear the locked record on a chat session. */
export const clearSessionLockedRecord = async (sessionId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/session/locked-record/clear`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.message || json?.error || 'Failed to unlock record');
  }
  return json.data || {};
};

/** Clear a pending Find Record UI request on the session. */
export const clearFindRecordRequest = async (sessionId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/session/find-record-request/clear`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.message || json?.error || 'Failed to clear find-record request');
  }
  return json.data || {};
};

/** Clear a pending embedding Wait/Queue UI request on the session. */
export const clearEmbeddingWaitRequest = async (sessionId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/session/embedding-wait-request/clear`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.message || json?.error || 'Failed to clear embedding-wait request');
  }
  return json.data || {};
};

/** Clear a pending tool_chain interactive pause on the session. */
export const clearToolChainWaitRequest = async (sessionId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/session/tool-chain-wait-request/clear`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.message || json?.error || 'Failed to clear tool-chain wait request');
  }
  return json.data || {};
};

/**
 * Resume a paused tool_chain after user confirm/edit/feedback.
 * @param {{ sessionId: string, previous_result?: object, feedback?: string }} opts
 */
export const resumeToolChainWait = async ({ sessionId, previous_result = null, feedback = null } = {}) => {
  if (!sessionId) throw new Error('sessionId is required');
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/session/tool-chain-wait/resume`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionId,
      previous_result: previous_result || undefined,
      feedback: feedback || undefined,
    }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.message || json?.error || 'Failed to resume tool chain');
  }
  return json.data;
};

/** Clear a pending entry_update confirmation on the session. */
export const clearEntryUpdateWaitRequest = async (sessionId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/session/entry-update-wait-request/clear`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.message || json?.error || 'Failed to clear entry-update wait request');
  }
  return json.data || {};
};

/**
 * Confirm a pending entry_update after user review.
 * @param {{ sessionId: string, field_changes?: Array }} opts
 */
export const confirmEntryUpdateWait = async ({ sessionId, field_changes = null } = {}) => {
  if (!sessionId) throw new Error('sessionId is required');
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/session/entry-update-wait/confirm`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionId,
      field_changes: field_changes || undefined,
    }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.message || json?.error || 'Failed to apply entry update');
  }
  return json.data || {};
};

/**
 * Queue embedding for a pending embedding_wait_request.
 * @param {{ sessionId: string, mode?: 'wait'|'queue', deferred_message?: string }} opts
 */
export const startEmbeddingWait = async ({ sessionId, mode = 'queue', deferred_message = null } = {}) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/session/embedding-wait/start`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionId,
      mode,
      ...(deferred_message != null ? { deferred_message } : {}),
    }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.message || json?.error || 'Failed to start embedding wait');
  }
  return json.data || {};
};

/** Query entries in an ingestion group (same endpoint as Data Management). */
export const queryGroupEntries = async ({
  suffix,
  groupSlug,
  filters = {},
  limit = 25,
  offset = 0,
  orderBy = 'created_at',
  orderDirection = 'desc',
} = {}) => {
  const headers = await getAuthHeaders();
  const url = `${API_BASE_URL}/casc/v1/api/suffixes/${encodeURIComponent(suffix)}/groups/${encodeURIComponent(groupSlug)}/entries/query`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ filters, limit, offset, orderBy, orderDirection }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    throw new Error(json?.error || json?.message || 'Failed to search entries');
  }
  const list = Array.isArray(json?.data)
    ? json.data
    : (Array.isArray(json?.items)
      ? json.items
      : (Array.isArray(json?.entries) ? json.entries : []));
  return {
    entries: list,
    totalCount: typeof json?.totalCount === 'number' ? json.totalCount : list.length,
  };
};

export const getResearchMeta = async () => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/research/meta`, { headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to load research metadata');
  }
  return json.data;
};

export const listResearchProjects = async ({ includeArchived = false, limit = 100 } = {}) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  if (includeArchived) params.set('includeArchived', 'true');
  if (limit) params.set('limit', String(limit));
  const qs = params.toString();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/research/projects${qs ? `?${qs}` : ''}`, { headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to list research projects');
  }
  return json.data || [];
};

export const getResearchProject = async (projectId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/research/projects/${encodeURIComponent(projectId)}`, { headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to load research project');
  }
  return json.data;
};

export const createResearchProject = async (body) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/research/projects`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to create research project');
  }
  return json.data;
};

export const updateResearchProject = async (projectId, body) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/research/projects/${encodeURIComponent(projectId)}`, {
    method: 'PUT',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to update research project');
  }
  return json.data;
};

export const archiveResearchProject = async (projectId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/research/projects/${encodeURIComponent(projectId)}/archive`, {
    method: 'POST',
    headers,
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to archive research project');
  }
  return json.data;
};

export const unarchiveResearchProject = async (projectId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/research/projects/${encodeURIComponent(projectId)}/unarchive`, {
    method: 'POST',
    headers,
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to unarchive research project');
  }
  return json.data;
};

export const runResearchProject = async (projectId, body = {}) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/research/projects/${encodeURIComponent(projectId)}/run`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to run research project');
  }
  return json.data;
};

export const createResearchJob = async (body) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/research/jobs`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to create research job');
  }
  return json.data || { id: json.jobId, status: json.status };
};

export const listResearchJobs = async ({ projectId, limit } = {}) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  if (projectId) params.set('projectId', projectId);
  if (limit) params.set('limit', String(limit));
  const qs = params.toString();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/research/jobs${qs ? `?${qs}` : ''}`, { headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to list research jobs');
  }
  return json.data || [];
};

export const getResearchJob = async (jobId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/research/jobs/${encodeURIComponent(jobId)}`, { headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to load research job');
  }
  return json.data;
};

export const getResearchSteps = async (jobId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/research/jobs/${encodeURIComponent(jobId)}/steps`, { headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to load research steps');
  }
  return json.data || [];
};

export const getResearchSources = async (jobId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/research/jobs/${encodeURIComponent(jobId)}/sources`, { headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to load research sources');
  }
  return json.data || [];
};

export const cancelResearchJob = async (jobId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/research/jobs/${encodeURIComponent(jobId)}/cancel`, {
    method: 'POST',
    headers,
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to cancel research job');
  }
  return json.data;
};

export const retryResearchJob = async (jobId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/research/jobs/${encodeURIComponent(jobId)}/retry`, {
    method: 'POST',
    headers,
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to retry research job');
  }
  return { job: json.data, rerun: !!json.rerun };
};

export const rerunResearchJob = async (jobId, body = {}) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/research/jobs/${encodeURIComponent(jobId)}/rerun`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to re-run research job');
  }
  return { job: json.data, rerun: !!json.rerun, ownerId: json.ownerId || null };
};

export const linkResearchToChat = async (jobId, projectId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/research/jobs/${encodeURIComponent(jobId)}/link-chat`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ project_id: projectId }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to open research in project chat');
  }
  return json.data;
};

export const fetchResearchIngestPlanes = async ({ jobId, projectId, suffix }) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({ project_id: projectId });
  if (suffix) params.set('suffix', suffix);
  const resp = await fetch(
    `${API_BASE_URL}/casc/v1/api/research/jobs/${encodeURIComponent(jobId)}/ingest/planes?${params}`,
    { headers }
  );
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to load ingestion planes');
  }
  return json.data || {};
};

export const planResearchIngest = async ({
  jobId,
  suffix,
  projectId,
  groupSlugs,
  field_selections,
  row_selections,
}) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/research/jobs/${encodeURIComponent(jobId)}/ingest/plan`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      suffix,
      project_id: projectId,
      group_slugs: groupSlugs,
      field_selections,
      row_selections,
    }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to plan research ingest');
  }
  return json.data || {};
};

export const fetchHeadlessUsers = async () => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/admin/headless/users`, { headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to load users');
  }
  return json.users || [];
};

export const fetchHeadlessOverview = async (sort = 'owner_user') => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/admin/headless/overview?sort=${encodeURIComponent(sort)}`, {
    headers,
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to load headless overview');
  }
  return json.data || {};
};

export const fetchHeadlessIntegrations = async () => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/admin/headless/integrations`, { headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to load integrations');
  }
  return json.integrations || [];
};

export const createHeadlessIntegration = async (payload) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/admin/headless/integrations`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to create integration');
  }
  return json;
};

export const updateHeadlessIntegration = async (id, payload) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/admin/headless/integrations/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to update integration');
  }
  return json.integration;
};

export const regenerateHeadlessSecret = async (id) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/admin/headless/integrations/${encodeURIComponent(id)}/regenerate-secret`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to regenerate secret');
  }
  return json;
};

export const fetchHeadlessAudit = async (id, limit = 50) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/admin/headless/integrations/${encodeURIComponent(id)}/audit?limit=${limit}`, {
    headers,
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to load audit events');
  }
  return json.events || [];
};

export const fetchDbMigrationConnection = async () => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/admin/db-migrations/connection`, { headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(json?.error || json?.message || 'Failed to load Postgres connection setting');
  }
  return json;
};

export const saveDbMigrationConnection = async (postgresUri) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/admin/db-migrations/connection`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ postgresUri }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to save Postgres connection');
  }
  return json;
};

export const fetchDbMigrationStatus = async () => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/admin/db-migrations/status`, { headers });
  const json = await resp.json().catch(() => ({}));
  if (resp.status === 503 && json?.configured === false) {
    return json;
  }
  if (!resp.ok) {
    throw new Error(json?.error || json?.message || 'Failed to load migration status');
  }
  return json;
};

export const runDbMigrations = async () => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/admin/db-migrations/run`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.result?.error || json?.message || 'Migration run failed');
  }
  return json;
};

// --- Email drafts ---

export const listEmailDrafts = async (suffix, { limit = 50, sessionId = null, status = null } = {}) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  if (limit) params.set('limit', String(limit));
  if (sessionId) params.set('sessionId', sessionId);
  if (status) params.set('status', status);
  const qs = params.toString();
  const resp = await fetch(
    `${API_BASE_URL}/casc/v1/api/email-drafts/suffixes/${encodeURIComponent(suffix)}${qs ? `?${qs}` : ''}`,
    { headers }
  );
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to list email drafts');
  }
  return json.data || [];
};

export const createEmailDraft = async (suffix, payload) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/email-drafts/suffixes/${encodeURIComponent(suffix)}`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload || {}),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to create email draft');
  }
  return json.data;
};

export const getEmailDraft = async (suffix, draftId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(
    `${API_BASE_URL}/casc/v1/api/email-drafts/suffixes/${encodeURIComponent(suffix)}/${encodeURIComponent(draftId)}`,
    { headers }
  );
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to load email draft');
  }
  return json.data;
};

export const updateEmailDraft = async (suffix, draftId, payload) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(
    `${API_BASE_URL}/casc/v1/api/email-drafts/suffixes/${encodeURIComponent(suffix)}/${encodeURIComponent(draftId)}`,
    {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload || {}),
    }
  );
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to update email draft');
  }
  return json.data;
};

export const deleteEmailDraft = async (suffix, draftId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(
    `${API_BASE_URL}/casc/v1/api/email-drafts/suffixes/${encodeURIComponent(suffix)}/${encodeURIComponent(draftId)}`,
    { method: 'DELETE', headers }
  );
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    throw new Error(json?.error || json?.message || 'Failed to delete email draft');
  }
  return true;
};

export const saveEmailDraftToGmail = async (suffix, draftId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(
    `${API_BASE_URL}/casc/v1/api/email-drafts/suffixes/${encodeURIComponent(suffix)}/${encodeURIComponent(draftId)}/gmail`,
    {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }
  );
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json?.success) {
    const err = new Error(json?.error || json?.message || 'Failed to save Gmail draft');
    err.code = json?.code || null;
    throw err;
  }
  return json.data;
};

export const downloadEmailDraftMarkdown = async (suffix, draftId) => {
  const headers = await getAuthHeaders();
  const resp = await fetch(
    `${API_BASE_URL}/casc/v1/api/email-drafts/suffixes/${encodeURIComponent(suffix)}/${encodeURIComponent(draftId)}/export/md`,
    { headers }
  );
  if (!resp.ok) {
    const json = await resp.json().catch(() => ({}));
    throw new Error(json?.error || json?.message || 'Failed to export email draft');
  }
  const blob = await resp.blob();
  const cd = resp.headers.get('content-disposition') || '';
  const match = /filename="([^"]+)"/.exec(cd);
  const filename = match?.[1] || `email-draft-${draftId}.md`;
  return { blob, filename };
};
